import { useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import { processEntityDataFromApi } from "@/lib/utils/crm/task-data-processing";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import {
  GetCompaniessDocument,
  GetCompanyDocument,
  CreateCompanyDocument,
  UpdateCompanyDocument,
  DeleteCompanyDocument,
  GetCompaniessQuery,
  GetCompanyQuery,
  CreateCompanyMutation,
  UpdateCompanyMutation,
  DeleteCompanyMutation,
  CompaniesInsertInput,
  Companies,
} from "@/src/gql/graphql";
import { ENHANCED_COMPANIES_CONFIG } from "@/features/companies/components/EnhancedCompaniesConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import type { CompanyEntity } from "@/lib/types/company";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import {
  EMPTY_INFINITE_RESULT,
  useStableMutationResult,
} from "@/lib/hooks/api/shared/stable-entity-hook-results";
// Company writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

function parseCompanyFromApi(node: Companies): CompanyEntity {
  return processEntityDataFromApi(node, ENHANCED_COMPANIES_CONFIG.fields);
}

// ============================================================================
// COMPANIES HOOKS
// ============================================================================

/**
 * Module-level so `usePaginatedCollection`'s result memo has stable deps — an
 * inline arrow would be a fresh allocation each render (MEMO_STABLE_PROPS).
 */
const selectCompaniesConnection = (data: GetCompaniessQuery) => data.companiesCollection;
const mapCompanyNode = (node: unknown): CompanyEntity => parseCompanyFromApi(node as Companies);

/**
 * Allocated ONCE: the object is a dependency of `useBuildActionContext`'s memo
 * and closes over nothing render-scoped. See the note in `contacts-graphql.ts`
 * for why this is a module constant rather than a `useMemo`.
 */
const COMPANY_ENTITY_HOOKS: EntityHooks<CompanyEntity> = {
  queryDocument: GetCompaniessDocument,
  usePaginated: (params?: Record<string, unknown>) =>
    // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
    usePaginatedCollection(
      GetCompaniessDocument,
      params,
      selectCompaniesConnection,
      mapCompanyNode
    ),
  useInfinite: () => EMPTY_INFINITE_RESULT,
  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetCompanyQuery>(GetCompanyDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || options?.enabled === false,
    });
    return useMemo(
      () => ({
        ...result,
        data: (result.data?.companiesCollection?.edges?.[0]?.node
          ? parseCompanyFromApi(result.data.companiesCollection.edges[0].node as Companies)
          : null) as CompanyEntity | undefined,
        isLoading: result.loading,
        error: (result.error as unknown as Error) ?? null,
        refetch: result.refetch,
      }),
      [result]
    );
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateCompanyMutation>(UpdateCompanyDocument);

    const mutateAsync = useCallback(
      async ({ id, ...input }: { id: string } & Partial<Companies>) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_COMPANIES_CONFIG.fields,
          { mode: "update" }
        );

        const { data, error } = await mutate({
          variables: { id, input: cleanInput },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the company.");

        const updated = data?.updatecompaniesCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the company.");
        return parseCompanyFromApi(updated as Companies);
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteCompanyMutation>(DeleteCompanyDocument);

    const mutateAsync = useCallback(
      async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't delete the company.");
        const deletedId = data?.deleteFromcompaniesCollection?.records?.[0]?.id ?? id;
        if (!deletedId) throw new Error("Couldn't delete the company.");
        return deletedId;
      },
      [mutate]
    );

    return useStableMutationResult(mutateAsync, result.loading);
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateCompanyMutation>(CreateCompanyDocument);
    const mutateAsync = useCallback(
      async (input: CompaniesInsertInput) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          ENHANCED_COMPANIES_CONFIG.fields,
          { mode: "insert" }
        );
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the company.");
        const created = data?.insertIntocompaniesCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the company.");
        return created as CompanyEntity;
      },
      [mutate]
    );
    return useStableMutationResult(mutateAsync, result.loading);
  },

  getFieldConfig: (fieldName: string) => {
    return ENHANCED_COMPANIES_CONFIG.fields[fieldName] || null;
  },

  getTableConfig: () => {
    return ENHANCED_COMPANIES_CONFIG;
  },
};

export const useCompanyHooks = (): EntityHooks<CompanyEntity> => COMPANY_ENTITY_HOOKS;
